// =============================================================================
// FILE: src/main.jsx
// MODULE: Core — Application Entry Point
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   The root file that mounts the React app into the DOM.
//   Establishes the provider tree — order matters:
//
//   PROVIDER ORDER (outermost → innermost):
//   1. QueryProvider     → TanStack Query cache (needed by all data hooks)
//   2. AuthProvider      → Session + user profile (needed by SubscriptionProvider)
//   3. SubscriptionProvider → Tier + billing state (needs auth context)
//   4. App               → Router + page components (needs all above)
//
//   Rules:
//   - Never change the nesting order without understanding the dependencies
//   - AuthProvider must be outside SubscriptionProvider
//   - QueryProvider must be outside everything (hooks use it)
//   - ThemeProvider is self-contained and can wrap or be inside App
//
// STRICT MODE:
//   React.StrictMode is enabled in development only.
//   It double-invokes renders and effects to surface side-effect bugs.
//   It has no effect in production builds.
// =============================================================================

import { createRoot } from 'react-dom/client'

// Global styles — must be imported before any component styles
import './index.css'

// Provider tree
import { QueryProvider }        from '@/providers/QueryProvider'
import { AuthProvider }         from '@/providers/AuthProvider'
import { SubscriptionProvider } from '@/providers/SubscriptionProvider'

// Root app component (router lives here)
import App from './App'

// ---------------------------------------------------------------------------
// Mount the app.
// document.getElementById('root') is the <div id="root"> in index.html.
// ---------------------------------------------------------------------------
const container = document.getElementById('root')

if (!container) {
  throw new Error(
    '[Arcus] Root container #root not found in index.html. ' +
    'Make sure index.html has <div id="root"></div>.'
  )
}

const root = createRoot(container)

root.render(
  <QueryProvider>
    <AuthProvider>
      <SubscriptionProvider>
        <App />
      </SubscriptionProvider>
    </AuthProvider>
  </QueryProvider>
)
